
import { create } from 'zustand';

/**
 * ARGOS Backtest / Live Equity Store
 * ----------------------------------
 * Responsibilities:
 * 1. Normalize WS payload ONCE
 * 2. Enforce stable data contract
 * 3. Do NOT derive chart logic
 * 4. Do NOT split backtest/live
 *
 * Final row shape GUARANTEED:
 * {
 *   id: number | null,
 *   date: 'YYYY-MM-DD',
 *   closeTime: string | null,
 *   equity: number,
 *   realized_pnl: number | null,
 *   daily_pnl: number | null,
 *   daily_return: number | null,
 *   source: 'BACKTEST' | 'LIVE'
 * }
 */

function normalizeDate(value) {
  if (!value) return null;

  let date = String(value);

  // If accidentally passed MM/DD/YY or MM/DD/YYYY, normalize it.
  if (date.includes('/')) {
    const [m, d, y] = date.split('/');
    const yyyy = y.length === 2 ? `20${y}` : y;

    return `${yyyy}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }

  // Handles YYYY-MM-DD or timestamp-like strings.
  return date.slice(0, 10);
}

function normalizeSource(value) {
  if (value === 'LIVE') return 'LIVE';
  return 'BACKTEST';
}

function normalizeNumber(value) {
  if (value === null || value === undefined || value === '') return null;

  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function normalizeRow(r) {
  if (!r) return null;

  const date = normalizeDate(
    r.date ??
    r.closeDate ??
    r.close_date
  );

  const equity = normalizeNumber(r.equity);

  if (!date || equity === null) {
    return null;
  }

  const realizedPnl = normalizeNumber(
    r.realized_pnl ??
    r.realizedPnl ??
    r.daily_pnl ??
    r.dailyPnl
  );

  return {
    id: r.id ?? r.tradeId ?? r.trade_id ?? null,
    date,
    closeTime: r.closeTime ?? r.close_time ?? null,
    equity,
    realized_pnl: realizedPnl,
    daily_pnl: realizedPnl,
    daily_return: normalizeNumber(r.daily_return ?? r.dailyReturn),
    source: normalizeSource(r.source),
  };
}

const useBacktestStore = create((set) => ({
  equitySeries: [],
  latestClosedEquity: null,

  /**
   * WS is authoritative.
   * We normalize ONCE here.
   */
  setArgosEquitySeries: (rows) =>
    set({
      equitySeries: Array.isArray(rows)
        ? rows.map(normalizeRow).filter(Boolean)
        : [],
    }),

  setArgosLatestClosedEquity: (value) =>
    set({
      latestClosedEquity: normalizeNumber(value),
    }),

  clearArgosEquitySeries: () =>
    set({
      equitySeries: [],
      latestClosedEquity: null,
    }),

  /**
   * Backward-compatible aliases in case existing components still call these.
   */
  setEquitySeries: (rows) =>
    set({
      equitySeries: Array.isArray(rows)
        ? rows.map(normalizeRow).filter(Boolean)
        : [],
    }),

  clearEquitySeries: () =>
    set({
      equitySeries: [],
      latestClosedEquity: null,
    }),
}));

export default useBacktestStore;

